"use client";

import { useEffect, useState } from "react";
import { fetchApi } from "./api-fetch";
import { apiFetchPath } from "./api-fetch-path";

const POLL_INTERVAL_MS = 60_000;

/**
 * Minimal live/offline signal for the nav. Fails silent — a schedule fetch
 * failure should never surface as broken chrome.
 *
 * `hasSchedule` is what decides whether the Live entry appears at all: with
 * nothing scheduled and nobody streaming, the tab would only lead to an empty
 * page, so it stays hidden until there is something to show. It starts false
 * so the tab is never briefly visible before the first poll resolves.
 */
export function useLiveStatus(): { isLive: boolean; hasSchedule: boolean } {
  const [isLive, setIsLive] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetchApi(apiFetchPath("/api/v1/live/schedule"));
        if (!res.ok) return;
        const data = (await res.json()) as { isLive?: unknown; upcoming?: unknown };
        if (cancelled) return;
        const live = typeof data.isLive === "boolean" ? data.isLive : false;
        const upcomingCount = Array.isArray(data.upcoming) ? data.upcoming.length : 0;
        setIsLive(live);
        setHasSchedule(live || upcomingCount > 0);
      } catch {
        // Silent — the nav pip simply stays hidden.
      }
    }

    void poll();
    const interval = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return { isLive, hasSchedule };
}
